import { Alert, AlertTitle, Button, Box } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useApproveDelegation, useRefuseDelegation } from '@/queries/useJobEnds';

interface DelegationApprovalBannerProps {
  jobEndId: number;
  collatorName: string;
  setSnackbar: (snackbar: { open: boolean; message: string; severity: 'success' | 'error' }) => void;
  rowVersion?: string;
}

export function DelegationApprovalBanner({
  jobEndId,
  collatorName,
  setSnackbar,
  rowVersion,
}: DelegationApprovalBannerProps) {
  const navigate = useNavigate();
  const { mutate: approve, isPending: isApproving } = useApproveDelegation();
  const { mutate: refuse, isPending: isRefusing } = useRefuseDelegation();
  const isPending = isApproving || isRefusing;

  const handleApprove = () => {
    approve({ id: jobEndId, rowVersion }, {
      onSuccess: () =>
        setSnackbar({
          open: true,
          message: 'Delegation approved. You are now the Job End author.',
          severity: 'success',
        }),
      onError: (error) =>
        setSnackbar({
          open: true,
          message: error.message,
          severity: 'error',
        }),
    });
  };

  const handleRefuse = () => {
    refuse({ id: jobEndId, rowVersion }, {
      onSuccess: () => {
        setSnackbar({
          open: true,
          message: `Delegation refused. The Job End remains with ${collatorName}.`,
          severity: 'success',
        });
        navigate('/');
      },
      onError: (error) =>
        setSnackbar({
          open: true,
          message: error.message,
          severity: 'error',
        }),
    });
  };

  return (
    <Alert
      severity="warning"
      sx={{
        mb: 3,
        '& .MuiAlert-message': {
          width: '100%',
        },
        '& .MuiAlert-action': {
          display: { xs: 'none', sm: 'flex' },
          alignItems: 'center',
          pt: 0,
        },
      }}
      action={
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            color="success"
            variant="contained"
            startIcon={<CheckIcon />}
            onClick={handleApprove}
            disabled={isPending}
          >
            Accept
          </Button>
          <Button
            color="error"
            variant="outlined"
            startIcon={<CloseIcon />}
            onClick={handleRefuse}
            disabled={isPending}
          >
            Reject
          </Button>
        </Box>
      }
    >
      <AlertTitle>Delegation request pending</AlertTitle>
      {collatorName} has requested to delegate this Job End to you. Please accept the request to
      gain editing access.
      <Box
        sx={{
          display: { xs: 'flex', sm: 'none' },
          flexDirection: 'column',
          gap: 1,
          mt: 2,
          width: '100%',
        }}
      >
        <Button
          color="success"
          variant="contained"
          startIcon={<CheckIcon />}
          onClick={handleApprove}
          disabled={isPending}
          fullWidth
        >
          Accept
        </Button>
        <Button
          color="error"
          variant="outlined"
          startIcon={<CloseIcon />}
          onClick={handleRefuse}
          disabled={isPending}
          fullWidth
        >
          Reject
        </Button>
      </Box>
    </Alert>
  );
}
